import { MOD_IDS, type AbaId, type Link, type ModId } from './types';
import { DEFS } from '../modulos/defs';
import { ctxDe } from '../modulos/ctx';
import { getDoc } from './store';
import { aba } from '../components/theme';
import { t } from '../lib/i18n';

/** Itens de dentro de cada aba que uma tarefa pode apontar. */
export function linkOptions(id: AbaId): { id: string; label: string; icon: string }[] {
  switch (id) {
    case 'anotacoes': return getDoc('anotacoes').boxes.filter(b => b.aba === 'anotacoes' && !b.archivedAt).map(b => ({ id: b.id, label: b.title || t('Sem título'), icon: '🗒️' }));
    case 'casa': return getDoc('casa').rooms.map(r => ({ id: r.id, label: r.name, icon: r.icon }));
    case 'estudos': return getDoc('estudos').ficharios.flatMap(f => f.materias.map(m => ({ id: m.id, label: `${f.nome} › ${m.nome}`, icon: m.icon })));
    case 'bemestar': { const b = getDoc('bemestar'); return [...b.refeicoes, ...b.cuidados].map(x => ({ id: x.id, label: x.nome, icon: x.icon })); }
    case 'garantias': return getDoc('garantias').items.map(w => ({ id: w.id, label: w.name, icon: '🧾' }));
    case 'financas': return getDoc('financas').contas.map(c => ({ id: c.id, label: c.nome, icon: c.icone }));
    case 'documentos': return getDoc('documentos').items.map(d => ({ id: d.id, label: d.nome || d.tipo, icon: '🪪' }));
    case 'monitor': {
      const m = getDoc('monitor');
      return [...m.profiles.map(p => ({ id: p.id, label: p.label, icon: '📰' })),
        ...m.hits.filter(h => h.status === 'confirmed').map(h => ({ id: h.hash, label: h.process || h.court || t('Publicação'), icon: '⚖️' }))];
    }
    default: {
      if (!(MOD_IDS as string[]).includes(id)) return [];
      // abas de fichas: tudo o que tem título (a viagem, a consulta, o cartão do Kanban...)
      const def = DEFS[id as ModId];
      const regs = getDoc(id as ModId)?.regs ?? [];
      const ctx = ctxDe(id as ModId, regs);
      const mapa = new Map(regs.map(r => [r.id, r]));
      return regs.flatMap(r => {
        const c = def.colecoes.find(x => x.id === r.col);
        if (!c || c.id === 'chamada') return [];
        const tit = c.titulo(r, ctx);
        if (!tit) return [];
        const pai = r.pai ? mapa.get(r.pai) : null;
        const cp = pai ? def.colecoes.find(x => x.id === pai.col) : null;
        return [{ id: r.id, label: pai && cp ? `${cp.titulo(pai, ctx)} › ${tit}` : tit, icon: typeof c.icone === 'function' ? c.icone(r) : c.icone }];
      });
    }
  }
}

/** O item vinculado ainda existe? (se foi apagado, a tarefa avisa) */
export function linkExists(l: NonNullable<Link>) {
  if (!l.id) return true;
  return linkOptions(l.aba).some(o => o.id === l.id);
}

export const linkLabel = (l: NonNullable<Link>) => `${aba(l.aba).nome}${l.id ? ' › ' + l.label : ''}`;
/** dia: a data da tarefa tocada no Planner (o Bem-Estar abre a refeição naquele dia, 0.47). */
export const linkHref = (l: NonNullable<Link>, dia?: string) =>
  `${l.aba === 'anotacoes' ? '/anotacoes' : `/aba/${l.aba}`}${l.id ? `?item=${encodeURIComponent(l.id)}${dia ? `&dia=${dia}` : ''}` : ''}`;
